// src/components/ProjectManagement/WorkerDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, updateDoc, doc, addDoc, getDocs } from 'firebase/firestore';

// Global cache for user tasks
let cachedUserTasks = [];
let lastFetchTime = 0;
let userIdCache = null;
const CACHE_DURATION = 30000; // 30 seconds
const ITEMS_PER_PAGE = 10; // Number of tasks per page

const AddRemarkModal = ({ task, onClose, onAdd, currentUser }) => {
  const [remark, setRemark] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (!remark.trim()) { 
      alert('Please enter a remark'); 
      return; 
    }
    setIsLoading(true);
    await onAdd(remark);
    setIsLoading(false);
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <h3>📝 Add Progress Update</h3>
        <p><strong>Task:</strong> {task.title}</p>
        <p><strong>Project:</strong> {task.projectName}</p>
        <label>Your Update / Remark</label>
        <textarea 
          value={remark} 
          onChange={(e) => setRemark(e.target.value)} 
          placeholder="e.g., I have finished the designs..." 
          rows="5" 
          style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', marginTop: '8px' }} 
        />
        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button onClick={handleSubmit} className="assign-btn" disabled={isLoading}>
            {isLoading ? 'Posting...' : '✓ Post Update'}
          </button>
          <button onClick={onClose} className="cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, itemCount, totalItems }) => {
  if (totalPages <= 1) return null;
  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };
  
  return (
    <div className="pagination-container" style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      padding: '12px 16px',
      marginTop: '12px',
      borderTop: '1px solid #eef2f6',
      flexWrap: 'wrap',
      gap: '10px'
    }}>
      <div className="pagination-info" style={{ fontSize: '12px', color: '#6b7280' }}>
        Showing {itemCount} of {totalItems} tasks
      </div>
      <div className="pagination-controls" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 ? 0.5 : 1
          }}
        >
          ← Prev
        </button>
        
        {getPageNumbers().map(pageNum => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: currentPage === pageNum ? 'none' : '1px solid #e5e7eb',
              background: currentPage === pageNum ? '#800020' : 'white',
              color: currentPage === pageNum ? 'white' : '#374151',
              cursor: 'pointer',
              fontWeight: currentPage === pageNum ? '600' : '400'
            }}
          >
            {pageNum}
          </button>
        ))}
        
        <button 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: 'white',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages ? 0.5 : 1
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

// Paginated Column Component
const PaginatedColumn = ({ title, tasks, color, onStartTask, onCompleteTask, onAddRemark, actionLoading, formatDuration, formatDate, statusType }) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [tasks.length]);
  
  const totalPages = Math.ceil(tasks.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTasks = tasks.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  const getColumnStyle = () => {
    switch(color) {
      case 'red': return { color: '#dc2626' };
      case 'orange': return { color: '#f59e0b' };
      case 'blue': return { color: '#3b82f6' };
      case 'green': return { color: '#10b981' };
      default: return {};
    }
  };
  
  const getColumnClass = () => {
    switch(statusType) {
      case 'overdue': return 'overdue-col';
      case 'pending': return 'pending-col';
      case 'ongoing': return 'ongoing-col';
      case 'completed': return 'completed-col';
      default: return '';
    }
  };
  
  return (
    <div className={`task-section ${getColumnClass()}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="section-header">
        <h4 style={getColumnStyle()}>{title} ({tasks.length})</h4>
      </div>
      
      <div style={{ flex: 1, overflowY: 'auto', minHeight: '300px' }}>
        {paginatedTasks.length === 0 ? (
          <div className="empty-tasks-message">
            <p>No {statusType} tasks</p>
            <small>
              {statusType === 'overdue' && 'Great job! No overdue tasks'}
              {statusType === 'pending' && 'All caught up!'}
              {statusType === 'ongoing' && 'Start a task to see it here'}
              {statusType === 'completed' && 'Completed tasks will appear here'}
            </small>
          </div>
        ) : (
          paginatedTasks.map(task => (
            <div key={task.id} className={`worker-task-card ${statusType}`}>
              <div className="task-header">
                <h4>{task.title}</h4>
                {statusType === 'overdue' && <span className="overdue-badge">OVERDUE</span>}
                {statusType === 'completed' && (
                  task.qcVerified ? 
                    <span className="verified-badge">✓ QC Verified</span> : 
                    <span className="pending-qc-badge">⏳ Awaiting QC</span>
                )}
              </div>
              {task.projectName && <div className="task-project-name">📁 {task.projectName}</div>}
              {task.description && <p>{task.description}</p>}
              <div className="task-meta">
                <span>⏱️ {formatDuration(task)}</span>
                {task.startDateObj && <span>🚀 Start: {formatDate(task.startDateObj)}</span>}
                {task.endDateObj && (
                  <span className={statusType === 'overdue' ? 'overdue-date' : ''}>
                    🏁 End: {formatDate(task.endDateObj)}
                  </span>
                )}
                {task.startedAtDate && <span>Started: {formatDate(task.startedAtDate)}</span>}
                {task.completedAtDate && <span>Completed: {formatDate(task.completedAtDate)}</span>}
              </div>
              {/* Start button appears for both Overdue and Pending */}
              {(statusType === 'overdue' || statusType === 'pending') && (
                <div className="task-actions-row">
                  <button onClick={() => onStartTask(task)} className="task-action-btn start-btn" disabled={actionLoading === task.id}>
                    {actionLoading === task.id ? '...' : '🚀 Start Task'}
                  </button>
                  <button onClick={() => onAddRemark(task)} className="task-action-btn remark-btn">📝 Add Remark</button>
                </div>
              )}
              {statusType === 'ongoing' && (
                <div className="task-actions-row">
                  <button onClick={() => onCompleteTask(task)} className="task-action-btn complete-btn" disabled={actionLoading === task.id}>
                    {actionLoading === task.id ? '...' : '✅ Mark Complete'}
                  </button>
                  <button onClick={() => onAddRemark(task)} className="task-action-btn remark-btn">📝 Add Remark</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      
      {tasks.length > ITEMS_PER_PAGE && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemCount={paginatedTasks.length}
          totalItems={tasks.length}
        />
      )}
    </div>
  );
};

const WorkerDashboard = ({ currentUser, userRole }) => {
  const [tasks, setTasks] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState('today');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showRemarkModal, setShowRemarkModal] = useState(null);
  
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Fetch user tasks with caching
  useEffect(() => {
    if (!currentUser) return;
    
    isMountedRef.current = true;
    const now = Date.now();
    
    if (cachedUserTasks.length > 0 && userIdCache === currentUser.uid && (now - lastFetchTime) < CACHE_DURATION) {
      setTasks(cachedUserTasks);
      setLoading(false);
    }
    
    const unsubscribe = onSnapshot(collection(db, 'tasks'), (snapshot) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      
      debounceTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        
        const tasksData = snapshot.docs
          .map(doc => ({ 
            id: doc.id, 
            ...doc.data(), 
            deadlineDate: doc.data().deadline?.toDate?.() || null, 
            completedAtDate: doc.data().completedAt?.toDate?.() || null, 
            startedAtDate: doc.data().startedAt?.toDate?.() || null, 
            startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null), 
            endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null) 
          }))
          .filter(task => {
            if (Array.isArray(task.assignedTo)) return task.assignedTo.includes(currentUser.uid);
            return task.assignedTo === currentUser.uid;
          });
        
        cachedUserTasks = tasksData;
        userIdCache = currentUser.uid;
        lastFetchTime = Date.now();
        
        if (isMountedRef.current) {
          setTasks(tasksData);
          setLoading(false);
        }
      }, 100);
    });
    
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      unsubscribe();
    };
  }, [currentUser, refreshKey]);

  const filterByPeriod = useCallback((task) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const weekStart = new Date(today); 
    weekStart.setDate(today.getDate() - today.getDay());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const startDate = task.startDateObj;
    const endDate = task.endDateObj || task.deadlineDate;
    
    switch(filterPeriod) {
      case 'today': {
        if (startDate && startDate.toDateString() === today.toDateString()) return true;
        if (endDate && endDate.toDateString() === today.toDateString()) return true;
        if (startDate && endDate && startDate <= today && endDate >= today) return true;
        return false;
      }
      case 'tomorrow': {
        if (startDate && startDate.toDateString() === tomorrow.toDateString()) return true;
        if (endDate && endDate.toDateString() === tomorrow.toDateString()) return true;
        if (startDate && endDate && startDate <= tomorrow && endDate >= tomorrow) return true;
        return false;
      }
      case 'week': {
        const weekEnd = new Date(today); 
        weekEnd.setDate(today.getDate() + (6 - today.getDay()));
        if (startDate && startDate >= weekStart && startDate <= weekEnd) return true;
        if (endDate && endDate >= weekStart && endDate <= weekEnd) return true;
        if (startDate && endDate && startDate <= weekEnd && endDate >= weekStart) return true;
        return false;
      }
      case 'month': {
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        if (startDate && startDate >= monthStart && startDate <= monthEnd) return true;
        if (endDate && endDate >= monthStart && endDate <= monthEnd) return true;
        if (startDate && endDate && startDate <= monthEnd && endDate >= monthStart) return true;
        return false;
      }
      case 'year': {
        const yearEnd = new Date(now.getFullYear(), 11, 31);
        if (startDate && startDate >= yearStart && startDate <= yearEnd) return true;
        if (endDate && endDate >= yearStart && endDate <= yearEnd) return true;
        if (startDate && endDate && startDate <= yearEnd && endDate >= yearStart) return true;
        return false;
      }
      default: return true;
    }
  }, [filterPeriod]);

  // Helper function to get the end date for sorting
  const getTaskEndDate = (task) => {
    const endDate = task.endDateObj || task.deadlineDate;
    if (!endDate) return new Date(8640000000000000);
    return endDate;
  };

  // Memoized filtering with overdue detection AND SORTING BY DATE
  const { overdueTasks, pendingTasks, ongoingTasks, completedTasks, allTasks } = useMemo(() => {
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let overdue = [];
    let pending = [];
    let ongoing = [];
    let completed = [];
    
    for (const task of tasks) {
      if (!filterByPeriod(task)) continue;
      
      let taskEndDate = task.endDateObj || task.deadlineDate;
      
      // CRITICAL FIX: Only check overdue for PENDING tasks (not ongoing)
      // Once a task is ongoing, it should never go back to overdue
      let isOverdue = false;
      if (task.status === 'pending' && taskEndDate) {
        const taskEndDateOnly = new Date(taskEndDate.getFullYear(), taskEndDate.getMonth(), taskEndDate.getDate());
        isOverdue = taskEndDateOnly < todayMidnight;
      }
      
      if (task.status === 'completed') {
        completed.push(task);
      } else if (task.status === 'ongoing') {
        // Ongoing tasks go to ongoing column regardless of date
        ongoing.push(task);
      } else if (task.status === 'pending') {
        if (isOverdue) {
          overdue.push(task);
        } else {
          pending.push(task);
        }
      }
    }
    
    // Sort all tasks by end date (earliest first)
    overdue.sort((a, b) => getTaskEndDate(a) - getTaskEndDate(b));
    pending.sort((a, b) => getTaskEndDate(a) - getTaskEndDate(b));
    ongoing.sort((a, b) => getTaskEndDate(a) - getTaskEndDate(b));
    completed.sort((a, b) => getTaskEndDate(b) - getTaskEndDate(a));
    
    return {
      overdueTasks: overdue,
      pendingTasks: pending,
      ongoingTasks: ongoing,
      completedTasks: completed,
      allTasks: [...overdue, ...pending, ...ongoing, ...completed]
    };
  }, [tasks, filterByPeriod]);

  // Status filter
  const getFilteredTasksByStatus = useCallback((tasksArray) => {
    if (filterStatus === 'all') return tasksArray;
    if (filterStatus === 'overdue') return tasksArray;
    if (filterStatus === 'pending') {
      return tasksArray.filter(t => {
        if (t.status !== 'pending') return false;
        const now = new Date();
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const taskEndDate = t.endDateObj || t.deadlineDate;
        if (taskEndDate) {
          const taskEndDateOnly = new Date(taskEndDate.getFullYear(), taskEndDate.getMonth(), taskEndDate.getDate());
          return taskEndDateOnly >= todayMidnight;
        }
        return true;
      });
    }
    if (filterStatus === 'ongoing') return tasksArray.filter(t => t.status === 'ongoing');
    if (filterStatus === 'completed') return tasksArray.filter(t => t.status === 'completed');
    return tasksArray;
  }, [filterStatus]);

  const filteredOverdue = getFilteredTasksByStatus(overdueTasks);
  const filteredPending = getFilteredTasksByStatus(pendingTasks);
  const filteredOngoing = getFilteredTasksByStatus(ongoingTasks);
  const filteredCompleted = getFilteredTasksByStatus(completedTasks);

  const handleStartTask = async (task) => {
    setActionLoading(task.id);
    try {
      // Update task status to ongoing
      await updateDoc(doc(db, 'tasks', task.id), { 
        status: 'ongoing', 
        startedAt: new Date(), 
        updatedAt: new Date() 
      });
      
      // Also remove any overdue flag by ensuring status is updated
      
      if (task.assignedBy) {
        await addDoc(collection(db, 'notifications'), { 
          userId: task.assignedBy, 
          type: 'task_started', 
          title: 'Task Started 🚀', 
          message: `${currentUser.displayName || currentUser.email?.split('@')[0] || 'A worker'} started "${task.title}"`, 
          taskId: task.id, 
          read: false, 
          createdAt: new Date() 
        });
      }
      
      alert('✅ Task started! Good luck!');
      setRefreshKey(prev => prev + 1);
    } catch (error) { 
      console.error(error); 
      alert('Failed to start task'); 
    } finally { 
      setActionLoading(null); 
    }
  };

  const handleCompleteTask = async (task) => {
    setActionLoading(task.id);
    try {
      await updateDoc(doc(db, 'tasks', task.id), { 
        status: 'completed', 
        completedAt: new Date(), 
        updatedAt: new Date(), 
        qcVerified: false 
      });
      
      const qcQuery = query(collection(db, 'users'), where('role', '==', 'quality-control'));
      const qcSnapshot = await getDocs(qcQuery);
      
      const notificationPromises = [];
      for (const qcDoc of qcSnapshot.docs) {
        notificationPromises.push(addDoc(collection(db, 'notifications'), { 
          userId: qcDoc.id, 
          type: 'task_completed', 
          title: 'Task Ready for QC Verification ✅', 
          message: `"${task.title}" has been completed and needs verification`, 
          taskId: task.id, 
          projectId: task.projectId, 
          completedBy: currentUser.uid, 
          completedByName: currentUser.displayName || currentUser.email?.split('@')[0], 
          assignmentNote: task.assignmentNote || '', 
          read: false, 
          createdAt: new Date() 
        }));
      }
      
      await Promise.all(notificationPromises);
      alert('✅ Task completed! QC has been notified for verification.');
      setRefreshKey(prev => prev + 1);
    } catch (error) { 
      console.error(error); 
      alert('Failed to complete task'); 
    } finally { 
      setActionLoading(null); 
    }
  };

  const handleAddRemark = async (task, remark) => {
    try {
      await addDoc(collection(db, 'taskRemarks'), { 
        taskId: task.id, 
        taskTitle: task.title, 
        projectName: task.projectName, 
        workerId: currentUser.uid, 
        workerName: currentUser.displayName || currentUser.email?.split('@')[0], 
        remark: remark, 
        createdAt: new Date() 
      });
      
      const managerQuery = query(collection(db, 'users'), where('role', 'in', ['manager', 'ceo', 'quality-control', 'workshop-manager']));
      const managerSnapshot = await getDocs(managerQuery);
      
      const notificationPromises = [];
      for (const managerDoc of managerSnapshot.docs) {
        notificationPromises.push(addDoc(collection(db, 'notifications'), { 
          userId: managerDoc.id, 
          type: 'task_remark', 
          title: '📝 Task Update', 
          message: `${currentUser.displayName || currentUser.email?.split('@')[0]} added an update on "${task.title}"`, 
          taskId: task.id, 
          projectName: task.projectName, 
          remark: remark, 
          read: false, 
          createdAt: new Date() 
        }));
      }
      
      await Promise.all(notificationPromises);
      alert('✅ Remark added successfully!');
      setShowRemarkModal(null);
    } catch (error) { 
      console.error(error); 
      alert('Failed to add remark'); 
    }
  };

  const formatDate = useCallback((date) => date ? date.toLocaleDateString() : 'No date', []);
  const formatDuration = useCallback((task) => {
    if (task.estimatedDays) return `${task.estimatedDays} day${task.estimatedDays !== 1 ? 's' : ''}`;
    if (task.startDateObj && task.endDateObj) {
      const days = Math.ceil((task.endDateObj - task.startDateObj) / (1000 * 60 * 60 * 24));
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return `${task.estimatedHours || 4} hours`;
  }, []);

  if (loading && cachedUserTasks.length === 0) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading your tasks...</p>
      </div>
    );
  }

  return (
    <div className="worker-dashboard">
      <div className="dashboard-header">
        <h2>📋 My Tasks</h2>
        <p>Welcome back, {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Worker'}! Here are your assigned tasks.</p>
      </div>

      <div className="dashboard-filters">
        <button className={`filter-btn ${filterPeriod === 'today' ? 'active' : ''}`} onClick={() => setFilterPeriod('today')}>📅 Today</button>
        <button className={`filter-btn ${filterPeriod === 'tomorrow' ? 'active' : ''}`} onClick={() => setFilterPeriod('tomorrow')}>🔮 Tomorrow</button>
        <button className={`filter-btn ${filterPeriod === 'week' ? 'active' : ''}`} onClick={() => setFilterPeriod('week')}>📆 This Week</button>
        <button className={`filter-btn ${filterPeriod === 'month' ? 'active' : ''}`} onClick={() => setFilterPeriod('month')}>📅 This Month</button>
        <button className={`filter-btn ${filterPeriod === 'year' ? 'active' : ''}`} onClick={() => setFilterPeriod('year')}>📅 This Year</button>
      </div>

      <div className="status-filters">
        <button className={`status-chip ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All ({allTasks.length})</button>
        <button className={`status-chip overdue ${filterStatus === 'overdue' ? 'active' : ''}`} onClick={() => setFilterStatus('overdue')}>🔴 Overdue ({filteredOverdue.length})</button>
        <button className={`status-chip pending ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>⏳ Pending ({filteredPending.length})</button>
        <button className={`status-chip ongoing ${filterStatus === 'ongoing' ? 'active' : ''}`} onClick={() => setFilterStatus('ongoing')}>⚙️ Ongoing ({filteredOngoing.length})</button>
        <button className={`status-chip completed ${filterStatus === 'completed' ? 'active' : ''}`} onClick={() => setFilterStatus('completed')}>✅ Completed ({filteredCompleted.length})</button>
      </div>

      <div className="tasks-grid-4cols">
        <PaginatedColumn 
          title="🔴 Overdue"
          tasks={filteredOverdue}
          color="red"
          statusType="overdue"
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onAddRemark={setShowRemarkModal}
          actionLoading={actionLoading}
          formatDuration={formatDuration}
          formatDate={formatDate}
        />

        <PaginatedColumn 
          title="⏳ Pending"
          tasks={filteredPending}
          color="orange"
          statusType="pending"
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onAddRemark={setShowRemarkModal}
          actionLoading={actionLoading}
          formatDuration={formatDuration}
          formatDate={formatDate}
        />

        <PaginatedColumn 
          title="⚙️ Ongoing"
          tasks={filteredOngoing}
          color="blue"
          statusType="ongoing"
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onAddRemark={setShowRemarkModal}
          actionLoading={actionLoading}
          formatDuration={formatDuration}
          formatDate={formatDate}
        />

        <PaginatedColumn 
          title="✅ Completed"
          tasks={filteredCompleted}
          color="green"
          statusType="completed"
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onAddRemark={setShowRemarkModal}
          actionLoading={actionLoading}
          formatDuration={formatDuration}
          formatDate={formatDate}
        />
      </div>

      {showRemarkModal && (
        <AddRemarkModal 
          task={showRemarkModal} 
          currentUser={currentUser} 
          onClose={() => setShowRemarkModal(null)} 
          onAdd={(remark) => handleAddRemark(showRemarkModal, remark)} 
        />
      )}
    </div>
  );
};

export default WorkerDashboard;